"use client"

import { useEffect, useState } from "react"
import API from "../services/api"
import "./EvenementsClient.css"

// Translation system
const translations = {
  fr: {
    // Header
    back: "Retour",

    // Welcome banner
    specialEvents: "Événements",
    special: "Spéciaux",
    discoverEvents: "Découvrez nos événements et activités",

    // Loading & Empty states
    loadingEvents: "Chargement des événements...",
    noEventsFound: "Aucun événement trouvé",
    comeBackSoon: "Revenez bientôt pour découvrir nos prochains événements",

    // Footer
    contact: "Contact",
    address: "Adresse",
    reservations: "Réservations",
    wifi: "Wi-Fi",
    followUs: "Suivez-nous",
    network: "Réseau",
    password: "Mot de passe",
    availableAtReception: "Réseau Accor",
    addressLine1: "Rue de Syrie",
    addressLine2: "1002 TUNIS",
    allRightsReserved: "Tous droits réservés",
    createdBy: "Créé par",
  },

  en: {
    // Header
    back: "Back",

    // Welcome banner
    specialEvents: "Special",
    special: "Events",
    discoverEvents: "Discover our events and activities",

    // Loading & Empty states
    loadingEvents: "Loading events...",
    noEventsFound: "No events found",
    comeBackSoon: "Come back soon to discover our upcoming events",

    // Footer
    contact: "Contact",
    address: "Address",
    reservations: "Reservations",
    wifi: "Wi-Fi",
    followUs: "Follow Us",
    network: "Network",
    password: "Password",
    availableAtReception: "accor network",
    addressLine1: "Rue de Syrie",
    addressLine2: "1002 TUNIS",
    allRightsReserved: "All rights reserved",
    createdBy: "Created by",
  },

  ar: {
    // Header
    back: "رجوع",

    // Welcome banner
    specialEvents: "فعاليات",
    special: "خاصة",
    discoverEvents: "اكتشف فعالياتنا وأنشطتنا",

    // Loading & Empty states
    loadingEvents: "جاري تحميل الفعاليات...",
    noEventsFound: "لم يتم العثور على فعاليات",
    comeBackSoon: "عد قريبًا لاكتشاف فعالياتنا القادمة",

    // Footer
    contact: "اتصل بنا",
    address: "العنوان",
    reservations: "الحجوزات",
    wifi: "واي فاي",
    followUs: "تابعونا",
    network: "الشبكة",
    password: "كلمة المرور",
    availableAtReception: "شبكة أكور",
    addressLine1: "شارع الكورنيش",
    addressLine2: "تونس، 3000 تونس",
    allRightsReserved: "جميع الحقوق محفوظة",
    createdBy: "تم إنشاؤه بواسطة",
  },
}

const languages = [
  { code: "fr", name: "Français", flag: "/images/fr-flag-v2.png" },
  { code: "en", name: "English", flag: "/images/en-flag-v2.png" },
  { code: "ar", name: "العربية", flag: "/images/ar-flag-v2.png" },
]

const EvenementsClient = () => {
  const [evenements, setEvenements] = useState([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [currentLanguage, setCurrentLanguage] = useState("fr")
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)

  // Get translation function
  const t = (key) => translations[currentLanguage][key] || translations.fr[key] || key

  const fetchEvenements = async () => {
    try {
      setIsLoading(true)
      const res = await API.get("/evenements")
      setEvenements(res.data)
      setIsLoaded(true)
    } catch (err) {
      console.error("Erreur chargement:", err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchEvenements()
    const timer = setTimeout(() => {
      setIsLoaded(true)
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0) // Scroll to the top of the page
  }, [])

  useEffect(() => {
    // Load saved language from localStorage
    const savedLanguage = localStorage.getItem("IBIS-language")
    if (savedLanguage && translations[savedLanguage]) {
      setCurrentLanguage(savedLanguage)
      // Update document direction for Arabic
      document.documentElement.dir = savedLanguage === "ar" ? "rtl" : "ltr"
      document.documentElement.lang = savedLanguage
    }
  }, [])

  // Language change handler
  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode)
    localStorage.setItem("IBIS-language", langCode)
    setShowLanguageDropdown(false)

    // Update document direction for Arabic
    document.documentElement.dir = langCode === "ar" ? "rtl" : "ltr"
    document.documentElement.lang = langCode
  }

  const getCurrentLanguage = () => languages.find((lang) => lang.code === currentLanguage)

  return (
    <div className={`hotel-app2 ${currentLanguage === "ar" ? "rtl" : "ltr"}`}>
      <style jsx>{`
        /* Language dropdown styles */
        .language-selector {
          position: absolute;
          top: 15px;
          left: 15px;
          z-index: 20;
        }
        
        .language-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          border-radius: 20px;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
        }
        
        .language-toggle:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: scale(1.05);
        }
        
        .language-flag {
          width: 20px;
          height: 15px;
          object-fit: cover;
          border-radius: 2px;
        }
        
        .language-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          background: rgba(0, 0, 0, 0.9);
          border-radius: 8px;
          padding: 8px 0;
          min-width: 150px;
          z-index: 1000;
          backdrop-filter: blur(10px);
          margin-top: 5px;
        }
        
        .language-option {
          display: flex;
          align-items: center;
          padding: 8px 16px;
          cursor: pointer;
          transition: background-color 0.2s;
          gap: 8px;
          color: white;
          font-size: 14px;
        }
        
        .language-option:hover {
          background: rgba(255, 255, 255, 0.1);
        }
        
        .language-option.active {
          background: rgba(255, 255, 255, 0.2);
        }
        
        .flag-small {
          width: 20px;
          height: 15px;
          object-fit: cover;
          border-radius: 2px;
        }

        /* RTL Support */
        .rtl {
          direction: rtl;
        }

        .rtl .language-selector {
          left: auto;
          right: 15px;
        }

        .rtl .language-dropdown {
          left: auto;
          right: 0;
          text-align: right;
        }

        .rtl .header-back-link {
          flex-direction: row-reverse;
        }

        .rtl .header-back-link svg {
          margin-left: 8px;
          margin-right: 0;
        }

        .rtl .welcome-banner h1 {
          text-align: right;
        }

        .rtl .welcome-banner p {
          text-align: right;
        }

        .rtl .empty-state {
          text-align: right;
        }

        .rtl .content-item-content {
          text-align: right;
        }

        .rtl .content-item-arrow {
          transform: scaleX(-1); /* Flip arrow for RTL */
        }

        .rtl .event-detail-content {
          text-align: right;
        }

        .rtl .footer-section {
          text-align: right;
        }

        .rtl .copyright {
          text-align: right;
        }

        .rtl .copyright a {
          margin-left: 0;
          margin-right: 5px;
        }
      `}</style>

      {/* Language Selector */}
      <div className="language-selector">
        <button className="language-toggle" onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}>
          <img
            src={getCurrentLanguage()?.flag || "/placeholder.svg"}
            alt={getCurrentLanguage()?.name}
            className="language-flag"
          />
          <span>{getCurrentLanguage()?.code.toUpperCase()}</span>
        </button>
        {showLanguageDropdown && (
          <div className="language-dropdown">
            {languages.map((lang) => (
              <div
                key={lang.code}
                className={`language-option ${currentLanguage === lang.code ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation()
                  changeLanguage(lang.code)
                }}
              >
                <img src={lang.flag || "/placeholder.svg"} alt={lang.name} className="flag-small" />
                <span>{lang.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <header className="app-header">
        <button
          className="header-back-link"
          onClick={() => {
            if (selectedEvent) {
              setSelectedEvent(null)
            } else {
              window.location.href = "/Home"
            }
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d={currentLanguage === "ar" ? "M5 12H19M19 12L12 5M19 12L12 19" : "M19 12H5M5 12L12 19M5 12L12 5"}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t("back")}
        </button>
        <div className="logo-container">
          <img src="/images/logo2.webp" alt="IBIS Logo" className="logo" />
        </div>
        <div></div>
      </header>
      <main className="app-main">
        <div className="welcome-banner">
          <h1>
            <span>{t("specialEvents")}</span> {t("special")}
          </h1>
          <p>{t("discoverEvents")}</p>
        </div>
        <div className="content-container">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t("loadingEvents")}</p>
            </div>
          ) : evenements.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h3>{t("noEventsFound")}</h3>
              <p>{t("comeBackSoon")}</p>
            </div>
          ) : selectedEvent ? (
            // ✅ Detail view of selected event
            <div className="event-detail">
              <div className="event-detail-image">
                <img
                  src={selectedEvent.image || "/placeholder.svg"}
                  alt={selectedEvent.name}
                  onError={(e) => {
                    e.target.src = `/placeholder.svg?height=120&width=300&text=${selectedEvent.name}`
                  }}
                />
              </div>
              <div className="event-detail-content">
                <h2>{selectedEvent.name}</h2>
                <p>{selectedEvent.description}</p>
                <p>{selectedEvent.price} TND</p>
              </div>
            </div>
          ) : (
            // ✅ Default grid view
            <div className={`content-grid ${isLoaded ? "loaded" : ""}`}>
              {evenements.map((evenement, index) => (
                <div
                  key={evenement._id}
                  className="content-item"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => setSelectedEvent(evenement)}
                >
                  <div className="content-item-image">
                    <img
                      src={evenement.image || "/placeholder.svg"}
                      alt={evenement.name}
                      onError={(e) => {
                        e.target.src = `/placeholder.svg?height=120&width=300&text=${evenement.name}`
                      }}
                    />
                  </div>
                  <div className="content-item-content">
                    <h3>{evenement.name}</h3>
                    {/* Description removed in card */}
                    <div className="content-item-arrow">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M12 4L20 12L12 20M4 12H20"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>{t("contact")}</h4>
            <p>+216 71 830 300</p>
            <p>H6144@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("address")}</h4>
            <p>{t("addressLine1")}</p>
            <p>{t("addressLine2")}</p>
          </div>
          <div className="footer-section">
            <h4>{t("reservations")}</h4>
            <p>+216 71 830 300</p>
            <p>H6144@accor.com</p>
          </div>
          <div className="footer-section">
            <h4>{t("wifi")}</h4>
             
            <p>
                {t("availableAtReception")}
            </p>
          </div>
          <div className="footer-section">
            <h4>{t("followUs")}</h4>
            <div className="social-links">
              <a href="https://www.facebook.com/ibisTunis" aria-label="Facebook">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                </svg>
              </a>
              <a href="https://www.instagram.com/ibistunis/" aria-label="Instagram">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
               
            </div>
          </div>
        </div>
        <div className="copyright">
          <p>
            © {new Date().getFullYear()} IBIS Tunis. {t("allRightsReserved")}.
            <br />
            {t("createdBy")}{" "}
            <a href="https://www.itbafa.com" target="_blank" rel="noopener noreferrer">
              <img
                src="/images/itbafa_logo_white.png"
                alt="ITBAFA Logo"
                style={{ height: "20px", verticalAlign: "middle", marginLeft: "5px" }}
              />
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}

export default EvenementsClient
